/**
 * A table of figures.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 */

import { isJsonArray, isJsonObject, readArray, readBoolean, readString } from '@/domain/json'
import type { JsonValue } from '@/domain/json'
import { readCsrdLink } from '@/domain/csrdLink'
import { emptyCell, type TableCell, type TableSection } from '@/domain/section'
import { readTableFit } from '@/sections/table/fit'
import { hasOwnLook, readTableLook } from '@/sections/table/look'
import { readColumnWidths } from '@/sections/table/widths'
import TableBlock from '@/sections/table/TableBlock.vue'
import TableInspector from '@/sections/table/TableInspector.vue'
import { readCellAlign, readCellColour } from '@/sections/table/edit'
import { readSpan } from '@/sections/table/spans'
import type { SectionDefinition } from '@/sections/types'

export { readTableFit } from '@/sections/table/fit'
export { readColumnWidths, equalColumnWidths, defaultImportedWidths } from '@/sections/table/widths'

/**
 * One cell.
 *
 * A bare string is accepted as well as an object, because an import writes
 * cells the short way and a document written that way still has to open.
 *
 * A setting is kept only when it is one this version offers and, for a
 * colour, only when it is safe to write into a stylesheet. Anything else is
 * dropped and the cell kept: losing a tint is nothing, losing a figure is not.
 */
function readCell(value: JsonValue): TableCell {
  if (typeof value === 'string') return { value }
  if (!isJsonObject(value)) return { value: '' }

  const cell: TableCell = { value: readString(value, 'value') }
  if (readBoolean(value, 'numeric')) cell.numeric = true
  if (readBoolean(value, 'strong')) cell.strong = true
  if (readBoolean(value, 'ruleAbove')) cell.ruleAbove = true
  if (readBoolean(value, 'ruleBelow')) cell.ruleBelow = true

  const align = readCellAlign(value['align'])
  const fill = readCellColour(value['fill'])
  const ink = readCellColour(value['ink'])
  if (align) cell.align = align
  if (fill) cell.fill = fill
  if (ink) cell.ink = ink

  // Read one cell at a time, without the table around it. Whether a span fits
  // is settled where the table is drawn, by `planOf`, which every reader of
  // a table goes through.
  const colspan = readSpan(value['colspan'])
  const rowspan = readSpan(value['rowspan'])
  if (colspan) cell.colspan = colspan
  if (rowspan) cell.rowspan = rowspan
  return cell
}

function readRow(value: JsonValue): TableCell[] {
  return isJsonArray(value) ? value.map(readCell) : []
}

export const tableSection: SectionDefinition<'table', TableSection> = {
  kind: 'table',
  band: 'content',

  icon: ['M2.5 3.5h11v9h-11z', 'M2.5 6.5h11', 'M6.5 6.5v6', 'M10 6.5v6'],

  create: (id, title) => ({
    id,
    kind: 'table',
    title,
    header: [emptyCell(), emptyCell()],
    rows: [
      [emptyCell(), emptyCell()],
      [emptyCell(), emptyCell()],
    ],
    caption: '',
    ruled: false,
    fit: 'normal',
    columnWidths: [],
  }),

  read: ({ id, title, stored }) => {
    const look = readTableLook(stored)
    const csrd = readCsrdLink(isJsonObject(stored) ? stored['csrd'] : undefined)
    const styleId = isJsonObject(stored) ? stored['styleId'] : undefined
    return {
      id,
      kind: 'table',
      title,
      header: readRow(isJsonObject(stored) ? (stored['header'] ?? null) : null),
      rows: readArray(stored, 'rows').map(readRow),
      caption: readString(stored, 'caption'),
      ruled: readBoolean(stored, 'ruled'),
      fit: readTableFit(stored),
      columnWidths: readColumnWidths(stored),
      // Only where it parts from the document, so a plain table stays plain in storage.
      ...(hasOwnLook(look) ? { look } : {}),
      // The style it follows: a whole number, 0 for none. Anything else is no choice made.
      ...(typeof styleId === 'number' && Number.isInteger(styleId) && styleId >= 0
        ? { styleId }
        : {}),
      // Only a link that can name its place in CSRD; otherwise an ordinary table.
      ...(csrd ? { csrd } : {}),
    }
  },

  block: TableBlock,
  inspector: TableInspector,
}
